/* eslint-disable upright/no-platform-color */
/* eslint-disable upright/no-primer-theme-get */
/* eslint-disable upright/no-primer-use-theme */
import { theme, themeGet as primerDefaultThemeGet, useTheme as primerDefaultUseTheme } from '@primer/react';
/* eslint-enable upright/no-primer-theme-get */
/* eslint-enable upright/no-primer-use-theme */
import { NestedKeyOf } from '@upright/common/utils/types';
import { PlatformColor } from '@upright/ui/common/generic/global/platform-colors';

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function deepmerge<T extends object, U extends object>(target: T, source: U): T & U {
  if (!isPlainObject(target) || !isPlainObject(source)) {
    return source as unknown as T & U;
  }
  const out: Record<string, unknown> = { ...target };
  for (const key of Object.keys(source)) {
    out[key] = isPlainObject(out[key]) && isPlainObject(source[key]) ? deepmerge(out[key], source[key]) : source[key];
  }
  return out as T & U;
}

/* CUSTOM THEME FOR PRIMER COMPONENT STYLES
 * See the full theme object here: https://primer.style/react/theme-reference
 * Ask RN if any questions!
 */

// This type does not conform to the actual object Primer creates, i.e. Primer has broken typings.
// The string lists "breakpoints", "fontSizes", "radii" and "space" are actually objects with number keys.
// Our own UprightCustom type fixes this.
type PrimerDefault = typeof theme;
type UprightCustom = typeof themeOverrides;

type ColorScheme = UprightCustom['colorSchemes'][keyof UprightCustom['colorSchemes']];
type ColorKey = NestedKeyOf<ColorScheme>;

type UprightTheme = Omit<PrimerDefault, keyof UprightCustom> & UprightCustom;
type ThemeWithoutColorSchemes = Omit<UprightTheme, 'colorSchemes'>;
type ThemeKeyWithoutColorSchemes = NestedKeyOf<ThemeWithoutColorSchemes>;

type ThemeGetKey = ThemeKeyWithoutColorSchemes | ColorKey;

export function themeGet(key: ThemeGetKey) {
  return primerDefaultThemeGet(key);
}

export function useTheme() {
  return primerDefaultUseTheme() as { theme?: UprightTheme & ColorScheme } & Omit<
    ReturnType<typeof primerDefaultUseTheme>,
    'theme'
  >;
}

const themeOverrides = {
  // BREAKPOINTS
  /* RN: For reference, these are the breakpoints in use.
   breakpoints: {
    0: '544px',
    1: '768px',
    2: '1012px',
    3: '1280px',
  }, */
  // TYPOGRAPHY
  fonts: {
    normal:
      '"Helvetica Neue", -apple-system, BlinkMacSystemFont, "Segoe UI", "Noto Sans", Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji"',
    mono: 'SFMono-Regular, Consolas, "Liberation Mono", Menlo, Courier, monospace',
    heading: '"Proto Grotesk", sans-serif',
  },
  space: {
    0: '0',
    1: '4px',
    2: '8px',
    3: '16px',
    4: '24px',
    5: '32px',
    6: '40px',
    7: '48px',
    8: '64px',
    9: '80px',
    10: '96px',
    11: '112px',
    12: '128px',
    13: '144px',
  },
  fontSizes: {
    0: '0.75rem', // 12px with default base size 16px
    1: '0.875rem', // 14px
    2: '1rem', // 16px
    3: '1.25rem', // 20px
    4: '1.5rem', // 24px
    5: '2rem', // 32px
    6: '2.5rem', // 40px
    7: '3rem', // 48px
    8: '3.5rem', // 56px
  },
  radii: {
    0: '0px',
    1: '2px', // Modified from 3px to 2px
    // Back to Primer's own 6px, which is also --border-radius__md, the design
    // system's medium radius. Only the styled-components-era Primer components
    // read this (Tooltip, ToggleSwitch, deprecated ActionList / FilteredSearch);
    // everything CSS-module-based already resolves --borderRadius-medium = 6px,
    // so this closes a 2px gap between the two halves of Primer rather than
    // introducing a new radius.
    2: '6px',
    3: '100px',
  },
  // COLORS
  // If you need impact colors or similar other colors not yet present here, please add them to the PlatformColor object and bring them here (but please have a good reason for adding colors to PlatformColor).
  colorSchemes: {
    /* ******** LIGHT THEME ******** */
    light: {
      colors: {
        accent: {
          fg: PlatformColor.FG_ACCENT, // We deviate here from Primer's blue foreground accent colors, since many components refer to this value. Essentially, the result is black/dark gray instead of blue.
          emphasis: PlatformColor.FG_ACCENT,
          muted: PlatformColor.BG_GRAY_DEFAULT,
          subtle: PlatformColor.BG_GRAY_LIGHTER,
        },
        // FOREGROUNDS
        fg: {
          default: PlatformColor.FG_DEFAULT,
          muted: PlatformColor.FG_GRAY_DEFAULT,
          subtle: PlatformColor.FG_GRAY_LIGHT,
          onEmphasis: PlatformColor.FG_WHITE,
        },
        // TEXT
        // Text added for easier accessibility.
        text: {
          default: PlatformColor.TEXT_BLACK,
          dark: PlatformColor.TEXT_GRAY_DARK,
          muted: PlatformColor.TEXT_GRAY,
          subtle: PlatformColor.TEXT_GRAY_LIGHT,
          onEmphasis: PlatformColor.TEXT_WHITE,
          disabled: PlatformColor.TEXT_DISABLED,
          blue: PlatformColor.TEXT_BLUE,
        },
        // Icon added for easier accessibility.
        icon: {
          dark: PlatformColor.ICON_GRAY_DARK,
          light: PlatformColor.ICON_GRAY_LIGHT,
          grayOnDarkBg: PlatformColor.ICON_GRAY_ON_DARK_BG,
          white: PlatformColor.FG_WHITE,
        },
        // BACKGROUNDS
        // Canvas is Primer-speak for bg.
        canvas: {
          default: PlatformColor.BG_WHITE,
          overlay: PlatformColor.BG_WHITE,
          inset: PlatformColor.BG_GRAY_LIGHT,
          subtle: PlatformColor.BG_GRAY_LIGHT,
        },
        // Bg added for easier accessibility.
        bg: {
          default: PlatformColor.BG_WHITE,
          gray: PlatformColor.BG_GRAY_DEFAULT,
          grayHover: PlatformColor.BG_GRAY_DEFAULT_HOVER,
          muted: PlatformColor.BG_GRAY_LIGHT,
          mutedHover: PlatformColor.BG_GRAY_LIGHT_HOVER,
          subtle: PlatformColor.BG_GRAY_LIGHTER,
        },

        impact: {
          positive: {
            dark: PlatformColor.IMPACT_POSITIVE_DARK,
            strong: PlatformColor.IMPACT_POSITIVE_STRONG,
            moderate: PlatformColor.IMPACT_POSITIVE_MODERATE,
            weak: PlatformColor.IMPACT_POSITIVE_WEAK,
            text: {
              light: PlatformColor.IMPACT_TEXT_POSITIVE_LIGHT,
              dark: PlatformColor.IMPACT_TEXT_POSITIVE_DARK,
            },
          },
          negative: {
            dark: PlatformColor.IMPACT_NEGATIVE_DARK,
            strong: PlatformColor.IMPACT_NEGATIVE_STRONG,
            moderate: PlatformColor.IMPACT_NEGATIVE_MODERATE,
            weak: PlatformColor.IMPACT_NEGATIVE_WEAK,
            text: {
              light: PlatformColor.IMPACT_TEXT_NEGATIVE_LIGHT,
              dark: PlatformColor.IMPACT_TEXT_NEGATIVE_DARK,
            },
          },
        },
        sdg: {
          positive: {
            alignedStrongFg: PlatformColor.SDG_ALIGNED_STRONG_FG,
            alignedModerateFg: PlatformColor.SDG_ALIGNED_MODERATE_FG,
            alignedWeakFg: PlatformColor.SDG_ALIGNED_WEAK_FG,
          },
          negative: {
            misalignedStrongFg: PlatformColor.SDG_MISALIGNED_STRONG_FG,
            misalignedModerateFg: PlatformColor.SDG_MISALIGNED_MODERATE_FG,
            misalignedWeakFg: PlatformColor.SDG_MISALIGNED_WEAK_FG,
          },
        },
        comparisons: {
          default: PlatformColor.DEFAULT_COMPARISON,
          benchmark: PlatformColor.BENCHMARK_COMPARISON,
        },
        /* BUTTONS
         * We use the default, primary, and danger variants without caveats.
         * We use the invisible variant ONLY with an icon.
         * We do not use the outline variant.
         */
        btn: {
          // Default:
          text: PlatformColor.BUTTON_DEFAULT_TEXT,
          bg: PlatformColor.BUTTON_DEFAULT_BG,
          border: PlatformColor.BUTTON_DEFAULT_BORDER,
          hoverBg: PlatformColor.BUTTON_DEFAULT_BG_HOVER,
          hoverBorder: PlatformColor.BUTTON_DEFAULT_BORDER,
          disabledText: PlatformColor.TEXT_DISABLED,
          // Primary:
          primary: {
            text: PlatformColor.BUTTON_PRIMARY_TEXT,
            bg: PlatformColor.BUTTON_PRIMARY_BG,
            border: PlatformColor.BUTTON_PRIMARY_BORDER,
            hoverBg: PlatformColor.BUTTON_PRIMARY_BG_HOVER,
            hoverBorder: PlatformColor.BORDER_GRAY_DEFAULT,
            selectedBg: PlatformColor.BUTTON_PRIMARY_BG_ACTIVE,
            disabledText: PlatformColor.TEXT_DISABLED,
            disabledBg: PlatformColor.BUTTON_DISABLED_BG,
            disabledBorder: PlatformColor.BORDER_GRAY_DEFAULT,
            icon: 'rgba(255,255,255,0.8)',
            counterBg: 'rgba(0,45,17,0.2)',
          },
        },
        border: {
          dark: PlatformColor.BORDER_GRAY_DARK,
          default: PlatformColor.BORDER_GRAY_DEFAULT,
          defaultActive: PlatformColor.BORDER_GRAY_DEFAULT_ACTIVE,
          muted: 'hsla(210,18%,87%,1)', // TODO maybe: Add as variable or use existing platformcolor
          subtle: 'rgba(31,35,40,0.15)', // TODO maybe: Add as variable or use existing platformcolor
        },
        // STATE COLORS
        info: {
          // I.e. "neutral", used in e.g. FlashPlatform component. We use "info" to avoid colliding with Primer's "neutral" key.
          fg: PlatformColor.STATE_NEUTRAL_FG,
          emphasis: PlatformColor.STATE_NEUTRAL_EMPHASIS,
          muted: PlatformColor.STATE_NEUTRAL_MUTED,
          subtle: PlatformColor.STATE_NEUTRAL_SUBTLE,
        },
        success: {
          fg: PlatformColor.STATE_SUCCESS_FG,
          emphasis: PlatformColor.STATE_SUCCESS_EMPHASIS,
          muted: PlatformColor.STATE_SUCCESS_MUTED,
          subtle: PlatformColor.STATE_SUCCESS_SUBTLE,
        },
        attention: {
          fg: PlatformColor.STATE_ATTENTION_FG,
          emphasis: PlatformColor.STATE_ATTENTION_EMPHASIS,
          muted: PlatformColor.STATE_ATTENTION_MUTED,
          subtle: PlatformColor.STATE_ATTENTION_SUBTLE,
        },
        danger: {
          fg: PlatformColor.STATE_DANGER_FG,
          emphasis: PlatformColor.STATE_DANGER_EMPHASIS,
          muted: PlatformColor.STATE_DANGER_MUTED,
          subtle: PlatformColor.STATE_DANGER_SUBTLE,
        },
        // Primer's ToggleSwitch draws a line (on) or circle (off) marker inside its track in these
        // colours, which reads as a stray glyph next to its "On"/"Off" label. Only ToggleSwitch reads
        // them, and the knob position and label already show the state.
        switchTrack: {
          fg: 'transparent',
          disabledFg: 'transparent',
          checked: {
            fg: 'transparent',
            disabledFg: 'transparent',
          },
        },
        primer: {
          fg: {
            disabled: PlatformColor.FG_DISABLED,
          },
          border: {
            active: PlatformColor.BORDER_ACCENT,
          },
        },
        facade: {
          bgWhite: '#F0F0F0',
          bgGray: '#EBEDEF', // Original F5F5F5
          bgBlack: '#232323',
          bgBlue: '#EFF7FF',
          bgPurple: '#C0B6EA',
          fgDarkGrayAlpha: 'rgba(36, 36, 36, 0.3)',
          borderLightGray: '#EBEDEF', // Original E7F3F8
          borderDarkGray: '#CECECE', // Original CECECE
          borderBlack: '#232323',
          textBlack: '#232323',
          textGray: '#CECECE',
          yellow: '#E4FF32',
        },
      },
    },
  },
} as const;

export const customTheme = deepmerge(theme, themeOverrides);
